// Attention on a new mention while the reader is not looking (PARITY P337; Windows FlashWindowEx,
// the Mac's Dock bounce): the tab title alternates with «(N) Te mencionaron» once a second until the
// page is in front again (core/attentionBlink.ts decides when it starts). The tab badge
// (notifications.tsx useTabBadge) keeps owning the real title; this only swaps it on the "on" beats
// and puts it back.
import { useEffect, useRef } from 'react';
import { AttentionBlink, attentionBlinkMillis } from '../../../core/attentionBlink';
import { t } from '../../../i18n/i18n';

function pageInFront(): boolean {
  return document.visibilityState === 'visible' && document.hasFocus();
}

let timer: ReturnType<typeof setInterval> | null = null;
let alert = '';
let base = '';

function stop(): void {
  if (timer) clearInterval(timer);
  timer = null;
  if (alert && document.title === alert) document.title = base;
  alert = '';
}

function start(mentions: number): void {
  stop();
  alert = AttentionBlink.alertTitle(mentions, t('web.extras.attention'));
  base = document.title;
  let on = false;
  const beat = () => {
    if (pageInFront()) return stop();
    // Whatever the badge wrote meanwhile is the new base.
    if (document.title !== alert) base = document.title;
    on = !on;
    document.title = on ? alert : base;
  };
  beat();
  timer = setInterval(beat, attentionBlinkMillis);
}

/** Watches the mention count; a rise while the tab is not in front starts the blink. */
export function useAttentionBlink(mentions: number): void {
  const previous = useRef(mentions);
  useEffect(() => {
    if (AttentionBlink.shouldStart(previous.current, mentions, pageInFront())) start(mentions);
    else if (timer && mentions === 0) stop();
    previous.current = mentions;
  }, [mentions]);
  useEffect(() => {
    const back = () => pageInFront() && stop();
    window.addEventListener('focus', back);
    document.addEventListener('visibilitychange', back);
    return () => {
      window.removeEventListener('focus', back);
      document.removeEventListener('visibilitychange', back);
      stop();
    };
  }, []);
}

/** For the capture script: is the blink running? */
export function attentionBlinking(): boolean {
  return timer !== null;
}
